import type { PaymentWithRelations } from '@/api/payments';
import { getPaymentEventLog } from '@/api/payments';
import type { EventLogEntry } from '@/components/event-timeline';

/** Prefer external_id for lineage labels; fall back to the payment UUID. */
export function lineageLabel(externalId: string | null | undefined, paymentId: string): string {
  return externalId?.trim() ? externalId : paymentId;
}

/**
 * Compose Payment History for reissue lineage. Only the payment's own events are
 * shown — the parent's history stays on the parent, reachable via the Status card
 * "Reissued from" link.
 *
 * - Own events (newest first); a REISSUED event gains a "Reissued as: …" detail link
 * - Children end with a synthetic "Reissued from: …" entry (oldest / origin of the
 *   child's life, before Payment Created)
 */
export async function composePaymentHistory(
  payment: PaymentWithRelations,
  fetchEventLog: (paymentId: string) => Promise<EventLogEntry[]> = getPaymentEventLog
): Promise<EventLogEntry[]> {
  const own = await fetchEventLog(payment.id);

  const enriched = own.map((entry) => {
    if (entry.event_type === 'PAYMENT_REISSUED' && payment.reissued_as_payment_id) {
      return {
        ...entry,
        detail_link: {
          prefix: 'Reissued as',
          payment_id: payment.reissued_as_payment_id,
          label: lineageLabel(payment.reissued_as_external_id, payment.reissued_as_payment_id),
        },
      };
    }
    return entry;
  });

  if (!payment.parent_payment_id) {
    return enriched;
  }

  const oldestOwnCreatedAt =
    enriched.length > 0 ? enriched[enriched.length - 1].created_at : payment.created_at;
  const syntheticCreatedAt = new Date(new Date(oldestOwnCreatedAt).getTime() - 1000).toISOString();

  const syntheticReissuedFrom: EventLogEntry = {
    id: `synthetic-reissued-from-${payment.id}`,
    event_type: 'PAYMENT_REISSUED',
    from_status: 'Returned',
    to_status: 'Reissued',
    created_at: syntheticCreatedAt,
    actor_type: 'SYSTEM',
    actor_id: 'system',
    source: 'reissue',
    detail_link: {
      prefix: 'Reissued from',
      payment_id: payment.parent_payment_id,
      label: lineageLabel(payment.parent_external_id, payment.parent_payment_id),
    },
  };

  return [...enriched, syntheticReissuedFrom];
}
